import { useFlag } from '@unleash/proxy-client-react';
import { useCallback, useEffect, useState } from 'react';
import { useIntercom } from 'react-use-intercom';
import styled from 'styled-components';

import { OrganizationType } from '../hooks/useAccessBadge';
import useAccessBadge from '../hooks/useAccessBadge';
import BackendService from '../services/backendService/backendService';
import { Button } from './_atoms/Button';
import BaseModal from './_molecules/BaseModal';
import OrganizationSelector, {
  SelectedLocationType,
  SelectedOrganizationType,
} from './_molecules/OrganizationSelector/OrganizationSelector';

export function useCountrySchoolSelectionV2() {
  const useCountrySchoolSelectionV2 = useFlag('useCountrySchoolSelectionV2');
  return {
    useCountrySchoolSelectionV2,
  };
}

const CountrySchoolSelectionPopupV2 = () => {
  const [isModalVisible, setIsModalVisible] = useState(true);
  const [isUpdatingSchoolInfo, setIsUpdatingSchoolInfo] = useState(false);

  const { showNewMessages } = useIntercom();
  const { accessBadge } = useAccessBadge();

  // Organization selector state
  const [
    selectedOrganization,
    setSelectedOrganization,
  ] = useState<SelectedOrganizationType | null>(null);
  const [organizationType, setOrganizationType] = useState<OrganizationType | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<SelectedLocationType | null>(
    null,
  );
  const [customOrganizationName, setCustomOrganizationName] = useState<string>();
  const [needsCustomOrganizationName, setNeedsCustomOrganizationName] = useState<boolean>(
    false,
  );
  const [locationFetchError, setLocationFetchError] = useState<string | undefined>();
  const [hideOrganizationTypePicker, setHideOrganizationTypePicker] = useState<boolean>(
    false,
  );

  // Effect to handle access badge values
  useEffect(() => {
    if (accessBadge?.user?.profileInfo?.role) {
      // Set organization type based on role
      const role = accessBadge.user.profileInfo.role;
      switch (role) {
        case 'Teacher':
        case 'School Administrator':
          setOrganizationType(OrganizationType.School);
          setHideOrganizationTypePicker(true);
          break;
        case 'District Administrator':
          setOrganizationType(OrganizationType.District);
          setHideOrganizationTypePicker(true);
          break;
        default:
          setOrganizationType(null);
          setHideOrganizationTypePicker(false);
          break;
      }
    }
  }, [accessBadge]);

  const handleChangeOrgType = useCallback(
    (newOrgType: OrganizationType | null) => {
      if (organizationType && organizationType !== newOrgType) {
        setSelectedOrganization(null);
        setCustomOrganizationName(undefined);
        setNeedsCustomOrganizationName(false);
      }
      setOrganizationType(newOrgType);
    },
    [organizationType, setNeedsCustomOrganizationName],
  );

  const handleChangeLocation = (location: SelectedLocationType | null) => {
    if (
      selectedLocation?.placeAutocomplete?.place_id !==
      location?.placeAutocomplete?.place_id
    ) {
      setSelectedOrganization(null);
      setCustomOrganizationName(undefined);
      setNeedsCustomOrganizationName(false);
    }
    setSelectedLocation(location);
  };

  const handleChangeOrganization = (organization: SelectedOrganizationType | null) => {
    setSelectedOrganization(organization);
  };

  const handleSubmit = useCallback(async () => {
    if (!accessBadge) return;
    setIsUpdatingSchoolInfo(true);
    try {
      if (selectedOrganization && !selectedOrganization.organization.custom) {
        await BackendService.instance.organizations.reselectOrganization(
          selectedOrganization.organization.id,
        );

        setIsModalVisible(false);
      } else if (customOrganizationName) {
        const orgId = accessBadge?.organization?.id;
        if (!orgId) {
          throw new Error('No active organization ID found');
        }

        // This may end up changing active organization
        await BackendService.instance.organizations.updateOrganizationInfo(orgId, {
          name: customOrganizationName,
          country: selectedLocation?.place?.country,
          region: selectedLocation?.place?.region,
          city: selectedLocation?.place?.city,
        });
        setIsModalVisible(false);

        if (selectedLocation?.place?.country === 'US') {
          showNewMessages(
            `Hey! I want to select my school, but it's not listed. 🙏\n\n▶️ School Name, Zip Code: `,
          );
        }
      }
    } finally {
      setIsUpdatingSchoolInfo(false);
    }
  }, [
    accessBadge,
    selectedOrganization,
    customOrganizationName,
    selectedLocation,
    showNewMessages,
  ]);

  const shouldShowNextButtonAfterOrgSelection = () => {
    if (selectedOrganization) {
      return true;
    }

    if (needsCustomOrganizationName && customOrganizationName) {
      return true;
    }

    const isInternationalOrIncomplete =
      selectedLocation?.place?.country && selectedLocation?.place?.country !== 'US';
    if (isInternationalOrIncomplete && customOrganizationName) {
      return true;
    }

    return false;
  };

  return (
    <BaseModal
      title={`Complete your profile`}
      visible={isModalVisible}
      maskClosable={false}
    >
      <FormContainer>
        <OrganizationSelector
          organizationType={organizationType}
          onSelectOrganizationType={handleChangeOrgType}
          onSelectLocation={handleChangeLocation}
          selectedLocation={selectedLocation}
          onSelectOrganization={handleChangeOrganization}
          selectedOrganization={selectedOrganization}
          customOrganizationName={customOrganizationName}
          onSetCustomOrganizationName={setCustomOrganizationName}
          locationFetchError={locationFetchError}
          onLocationFetchError={setLocationFetchError}
          needsCustomOrganizationName={needsCustomOrganizationName}
          onSetNeedsCustomOrganizationName={setNeedsCustomOrganizationName}
          hideOrganizationTypePicker={hideOrganizationTypePicker}
          fluid
          metricEventNamePrefix="CountrySchoolPopup"
          disableSearchRetry={false}
        />

        {shouldShowNextButtonAfterOrgSelection() && (
          <Button
            onClick={handleSubmit}
            styles={{ marginTop: 20 }}
            disabled={isUpdatingSchoolInfo}
          >
            Continue
          </Button>
        )}
      </FormContainer>
    </BaseModal>
  );
};

export default CountrySchoolSelectionPopupV2;

const FormContainer = styled.div`
  margin-top: 20px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: center;
  justify-content: center;
`;
